import { useState, useEffect, useRef } from 'react';
import contactService from '../../services/contactService';

/**
 * Contact Selector Component
 * Reusable autocomplete select for choosing/creating contacts
 * Used in invoice creation and other forms
 * Style: Simple and responsive
 */
export default function ContactSelector({ onContactSelect, onCreateNew, selectedContact = null }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(selectedContact);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        performSearch();
      } else {
        setResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Close results when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowResults(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const performSearch = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await contactService.searchContacts(searchQuery, '', 20);
      setResults(response.data || []);
      setShowResults(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectContact = (contact) => {
    setSelected(contact);
    setSearchQuery(contact.name);
    setShowResults(false);
    if (onContactSelect) {
      onContactSelect(contact);
    }
  };

  const handleCreateNew = () => {
    setShowResults(false);
    if (onCreateNew) {
      onCreateNew();
    }
  };

  const handleClear = () => {
    setSelected(null);
    setSearchQuery('');
    setResults([]);
    setShowResults(false);
    if (onContactSelect) {
      onContactSelect(null);
    }
  };

  return (
    <div className="contact-selector" ref={containerRef}>
      <div className="selector-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setShowResults(results.length > 0)}
          placeholder="Rechercher un contact..."
          className="selector-input"
          autoComplete="off"
        />
        {selected && (
          <button
            type="button"
            className="clear-button"
            onClick={handleClear}
            title="Clear selection"
          >
            ✕
          </button>
        )}
        {loading && <span className="loading-spinner">⟳</span>}
      </div>

      {error && (
        <div className="selector-error">
          {error}
        </div>
      )}

      {showResults && (
        <div className="selector-results">
          {results.length > 0 ? (
            <>
              <div className="results-list">
                {results.map((contact) => (
                  <button
                    key={contact.id}
                    type="button"
                    className="result-item"
                    onClick={() => handleSelectContact(contact)}
                  >
                    <div className="result-main">
                      <span className="result-name">{contact.name}</span>
                      <span className={`result-badge result-type-${contact.type}`}>
                        {contact.type === 'individual' ? 'Particulier' : 'Société'}
                      </span>
                    </div>
                    {contact.email && (
                      <span className="result-email">{contact.email}</span>
                    )}
                    {contact.company_name && (
                      <span className="result-company">{contact.company_name}</span>
                    )}
                  </button>
                ))}
              </div>

              <div className="results-footer">
                <button
                  type="button"
                  className="btn-create-new"
                  onClick={handleCreateNew}
                >
                  + Créer un nouveau contact
                </button>
              </div>
            </>
          ) : (
            <div className="no-results">
              Aucun contact trouvé
              <button
                type="button"
                className="btn-create-new"
                onClick={handleCreateNew}
              >
                + Créer un nouveau contact
              </button>
            </div>
          )}
        </div>
      )}

      {selected && (
        <div className="selected-contact-info">
          <div className="info-row">
            <span className="label">Contact sélectionné:</span>
            <span className="value">{selected.name}</span>
          </div>
          {selected.email && (
            <div className="info-row">
              <span className="label">Email:</span>
              <span className="value">{selected.email}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
